// Customer Form Component

import { useState, useEffect } from 'react';
import type { Customer, CustomerFormData } from '@/lib/types';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import ImageUpload from '@/components/ui/ImageUpload';
import { isValidEmail, slugify } from '@/lib/utils';

interface CustomerFormProps {
  customer: Customer | null;
  onSubmit: (data: CustomerFormData) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export default function CustomerForm({
  customer,
  onSubmit,
  onCancel,
  isLoading,
}: CustomerFormProps) {
  const [formData, setFormData] = useState<CustomerFormData>({
    name: '',
    slug: '',
    forward_email: '',
    report_email: '',
    software_only: false,
    auto_report: true,
    forward_cc: [],
    active: true,
    logo_url: '',
    brand_color: '#667eea',
    stripe_price_id: '',
  });

  const [ccInput, setCcInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form with customer data when editing
  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name,
        slug: customer.slug,
        forward_email: customer.forward_email,
        report_email: customer.report_email || '',
        software_only: customer.software_only || false,
        auto_report: customer.auto_report !== false,
        forward_cc: customer.forward_cc,
        active: customer.active,
        logo_url: customer.logo_url || '',
        brand_color: customer.brand_color || '#667eea',
        stripe_price_id: customer.stripe_price_id || '',
      });
      setCcInput(customer.forward_cc.join(', '));
    }
  }, [customer]);

  // Auto-generate slug from name
  const handleNameChange = (name: string) => {
    setFormData((prev) => ({
      ...prev,
      name,
      // Only auto-generate slug if creating new customer
      slug: customer ? prev.slug : slugify(name),
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name ist erforderlich';
    }

    if (!formData.slug.trim()) {
      newErrors.slug = 'Slug ist erforderlich';
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = 'Slug darf nur Kleinbuchstaben, Zahlen und Bindestriche enthalten';
    }

    if (!formData.forward_email.trim()) {
      newErrors.forward_email = 'E-Mail ist erforderlich';
    } else if (!isValidEmail(formData.forward_email)) {
      newErrors.forward_email = 'Ungültige E-Mail-Adresse';
    }

    // Mehrere Berichts-Empfänger mit Komma erlaubt (z. B. Inhaber + Büro)
    if (formData.report_email) {
      const berichtEmails = formData.report_email.split(',').map((e) => e.trim());
      const invalid = berichtEmails.find((email) => email && !isValidEmail(email));
      if (invalid) newErrors.report_email = `Ungültige E-Mail-Adresse: ${invalid}`;
    }

    // Validate CC emails
    if (ccInput.trim()) {
      const ccEmails = ccInput.split(',').map((e) => e.trim());
      const invalidCc = ccEmails.find((email) => email && !isValidEmail(email));
      if (invalidCc) {
        newErrors.forward_cc = `Ungültige E-Mail-Adresse: ${invalidCc}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    // Parse CC emails
    const ccEmails = ccInput
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e);

    onSubmit({
      ...formData,
      forward_cc: ccEmails,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        label="Kundenname"
        value={formData.name}
        onChange={(e) => handleNameChange(e.target.value)}
        placeholder="z.B. Schreinerei Thomas"
        required
        error={errors.name}
        disabled={isLoading}
      />

      <Input
        label="Slug"
        value={formData.slug}
        onChange={(e) => setFormData({ ...formData, slug: slugify(e.target.value) })}
        placeholder="z.B. schreinerei-thomas"
        required
        error={errors.slug}
        hint="Eindeutiger Identifier für API-Zugriff (nur Kleinbuchstaben, Zahlen, Bindestriche)"
        disabled={isLoading}
      />

      <Input
        label="E-Mail für Weiterleitung"
        type="email"
        value={formData.forward_email}
        onChange={(e) => setFormData({ ...formData, forward_email: e.target.value })}
        placeholder="leads@kunde.de"
        required
        error={errors.forward_email}
        hint="Haupt-E-Mail-Adresse, an die Leads weitergeleitet werden"
        disabled={isLoading}
      />

      <Input
        label="CC E-Mail-Adressen (optional)"
        value={ccInput}
        onChange={(e) => setCcInput(e.target.value)}
        placeholder="email1@kunde.de, email2@kunde.de"
        error={errors.forward_cc}
        hint="Mehrere E-Mail-Adressen mit Komma trennen"
        disabled={isLoading}
      />

      <Input
        label="E-Mail für Monatsberichte (optional)"
        value={formData.report_email || ''}
        onChange={(e) => setFormData({ ...formData, report_email: e.target.value.trim() })}
        placeholder="inhaber@kunde.de, buero@kunde.de"
        error={errors.report_email}
        hint="Persönliche Adresse des Inhabers/Ansprechpartners, mehrere mit Komma trennen. BEWUSST getrennt von der Weiterleitungs-Adresse oben — die zeigt oft auf ein Sammelpostfach wie anfrage@. Leer = beim Freigeben geht keine Mail raus."
        disabled={isLoading}
      />

      <div className="flex items-start space-x-3 pt-2">
        <input
          type="checkbox"
          id="software_only"
          checked={!!formData.software_only}
          onChange={(e) => setFormData({ ...formData, software_only: e.target.checked })}
          className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          disabled={isLoading}
        />
        <label htmlFor="software_only" className="text-sm text-gray-700">
          Software-/Plattformkunde ohne Marketing-Projekte
          <span className="block text-xs text-gray-500 mt-0.5">
            Nur Login und Abrechnung — kein Tracking, keine Search Console, keine Kampagnen.
            Verhindert, dass fehlende Anbindungen als offene Aufgaben gemeldet werden.
          </span>
        </label>
      </div>

      <div className="flex items-start space-x-3 pt-2">
        <input
          type="checkbox"
          id="auto_report"
          checked={formData.auto_report !== false}
          onChange={(e) => setFormData({ ...formData, auto_report: e.target.checked })}
          className="mt-0.5 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          disabled={isLoading}
        />
        <label htmlFor="auto_report" className="text-sm text-gray-700">
          Automatischer Monatsbericht
          <span className="block text-xs text-gray-500 mt-0.5">
            Am 1. des Monats wird ein Berichtsentwurf für den Vormonat erzeugt (Freigabe bleibt
            immer manuell). Haken raus für Demo- und Software-Kunden — manuell erzeugen geht trotzdem.
          </span>
        </label>
      </div>

      {/* Abrechnung */}
      <div className="pt-4 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-3">💳 Abrechnung (optional)</h3>
        <Input
          label="Stripe Price-ID"
          value={formData.stripe_price_id || ''}
          onChange={(e) => setFormData({ ...formData, stripe_price_id: e.target.value.trim() })}
          placeholder="price_..."
          hint="Individueller Abo-Preis für diesen Kunden aus Stripe (Produktkatalog → Preis kopieren). Leer lassen für den Standard-Preis."
          disabled={isLoading}
        />
      </div>

      {/* Logo & Branding */}
      <div className="pt-4 border-t border-gray-200">
        <h3 className="text-sm font-medium text-gray-900 mb-3">🎨 Branding (optional)</h3>
        
        <div className="space-y-4">
          {/* Logo Upload */}
          <ImageUpload
            value={formData.logo_url}
            onChange={(url) => setFormData({ ...formData, logo_url: url })}
            label="Firmenlogo"
            folder={`customers/${formData.slug || 'new'}`}
          />

          {/* Markenfarbe */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Markenfarbe
            </label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={formData.brand_color || '#667eea'}
                onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                className="h-10 w-20 rounded border border-gray-300 cursor-pointer"
                disabled={isLoading}
              />
              <input
                type="text"
                value={formData.brand_color || '#667eea'}
                onChange={(e) => setFormData({ ...formData, brand_color: e.target.value })}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                placeholder="#667eea"
                disabled={isLoading}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Primärfarbe für das Kunden-Dashboard
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center space-x-3">
        <input
          type="checkbox"
          id="active"
          checked={formData.active}
          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          disabled={isLoading}
        />
        <label htmlFor="active" className="text-sm text-gray-700">
          Kunde ist aktiv (nur aktive Kunden erhalten Lead-Weiterleitungen)
        </label>
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button type="submit" loading={isLoading}>
          {customer ? 'Speichern' : 'Erstellen'}
        </Button>
      </div>
    </form>
  );
}
